import React, { useEffect, useState } from 'react'
import { getA2fHealth, subscribeA2fHealth, dismissA2fWarning } from '../lib/a2fHealth'

/**
 * A2FWarningIndicator — viewport pill shown when sentences deliver audio but no
 * ARKit animation frames, i.e. the A2F service isn't producing visemes and the
 * avatar is talking with a frozen face.
 *
 * Why this exists: on 2026-07-27 the A2F middleware was pointed at a dead
 * backend, so EVERY character lost lip-sync while audio kept playing. Nothing
 * in the UI said so, so it was reported as an avatar animation bug and cost
 * hours of frontend investigation before anyone looked at the service. The
 * client could always tell (audio bytes, zero frames) — it just never said.
 *
 * A service fault, not transient progress, so it's persistent and dismissible
 * like OGSAssetWarningIndicator (not auto-hiding like TextureUpgradeIndicator).
 * Red rather than amber: this is an outage, not a capability downgrade. Offset
 * below both existing pills so the three never overlap.
 */
export default function A2FWarningIndicator() {
  const [health, setHealth] = useState(getA2fHealth)
  useEffect(() => subscribeA2fHealth(setHealth), [])

  if (health.state !== 'a2f-no-frames' || health.dismissed) return null

  return (
    <div style={{
      position: 'absolute', top: 76, right: 12, zIndex: 32,
      background: 'var(--m-overlay)', border: '1px solid var(--m-danger)',
      borderRadius: 4, padding: '6px 10px', maxWidth: 340,
      color: 'var(--m-danger)', fontFamily: 'var(--m-font)', fontSize: 11,
      display: 'flex', alignItems: 'center', gap: 8, pointerEvents: 'auto',
    }} data-testid="a2f-warning">
      <span>
        No lip-sync: audio is playing but the A2F service sent no animation
        frames ({health.consecutiveAudioNoFrames} sentences). The avatar is fine —
        check the A2F service.
      </span>
      <button
        onClick={dismissA2fWarning}
        aria-label="Dismiss"
        style={{ background: 'none', border: 'none', color: 'var(--m-danger)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}
      >
        ×
      </button>
    </div>
  )
}
